<template>
  <div v-if="rule && !editing">
    <div
      v-if="rule.offer"
      class="font-weight-bold h6"
    >
      {{ rule.offer?.name || '' }}
    </div>
    <div
      v-if="rule.day"
      class="d-flex align-items-center justify-content-between"
    >
      <div class="font-weight-bold pb-2 text-primary">
        {{ formattedDay }}
      </div>
    </div>
    <div>
      {{ formatHour(rule.startMinutes) }} - {{ formatHour(rule.startMinutes + rule.durationMinutes) }}
    </div>
    <div
      v-if="rule.comment"
      class="text-secondary small mt-1"
    >
      {{ rule.comment }}
    </div>

    <hr>
    <div class="row">
      <div class="col-4">
        <button
          class="btn btn-secondary btn-sm btn-block"
          @click="$emit('cancel')"
        >
          Anuluj
        </button>
      </div>
      <div class="col-4">
        <button
          class="btn btn-outline-primary btn-sm btn-block"
          @click="startEditing"
        >
          Edytuj
        </button>
      </div>
      <div class="col-4">
        <button
          class="btn btn-danger btn-sm btn-block"
          :disabled="removePending"
          @click="remove"
        >
          Usuń
        </button>
      </div>
    </div>
  </div>

  <div v-else-if="rule && editing">
    <div class="font-weight-bold h6">
      {{ rule.offer?.name || "" }}
    </div>
    <div class="font-weight-bold pb-2 text-primary">
      {{ formattedDay }}
    </div>

    <div class="row">
      <div class="col-6">
        <div class="font-weight-bold text-left">
          Od
        </div>
        <BFormInput
          v-model="hourFrom"
          type="text"
          placeholder="HH:mm"
          :state="validateHour(hourFrom) ? null : false"
          @blur="updateHourFrom"
        />
      </div>
      <div class="col-6">
        <div class="font-weight-bold text-left">
          Do
        </div>
        <BFormInput
          v-model="hourTo"
          type="text"
          placeholder="HH:mm"
          :state="validateHour(hourTo, hourFrom) ? null : false"
          @blur="updateHourTo"
        />
      </div>
    </div>

    <div class="mt-2">
      <div class="font-weight-bold text-left">
        Komentarz
      </div>
      <BFormInput
        v-model="comment"
        type="text"
        placeholder="Komentarz (opcjonalnie)"
      />
    </div>

    <hr>
    <div class="row">
      <div class="col-6">
        <button
          class="btn btn-secondary btn-block"
          @click="editMode ? $emit('cancel') : editing = false"
        >
          Anuluj
        </button>
      </div>
      <div class="col-6">
        <button
          class="btn btn-primary btn-block"
          :disabled="editInvalid || savePending"
          @click="saveEdit"
        >
          Zapisz
        </button>
      </div>
    </div>
  </div>

  <div v-else>
    <div class="font-weight-bold h6">
      Dodaj powtarzające się zajęcia
    </div>

    <div class="d-flex align-items-center justify-content-between">
      <div
        v-if="initDay"
        class="font-weight-bold pb-2 text-primary"
      >
        {{ formattedDay }}
      </div>
      <BFormSelect
        v-else
        v-model="selectedWeekDay"
        :options="weekDayOptions"
        class="mb-2"
      />
    </div>

    <div class="row">
      <div class="col-6">
        <div class="font-weight-bold text-left">
          Od
        </div>
        <BFormInput
          v-model="hourFrom"
          type="text"
          placeholder="HH:mm"
          :state="validateHour(hourFrom) ? null : false"
          @blur="updateHourFrom"
        />
      </div>
      <div class="col-6">
        <div class="font-weight-bold text-left">
          Do
        </div>
        <BFormInput
          v-model="hourTo"
          type="text"
          placeholder="HH:mm"
          :state="validateHour(hourTo, hourFrom) ? null : false"
          @blur="updateHourTo"
        />
      </div>
    </div>

    <div class="mt-2">
      <div class="font-weight-bold text-left">
        Komentarz
      </div>
      <BFormInput
        v-model="comment"
        type="text"
        placeholder="Komentarz (opcjonalnie)"
      />
    </div>

    <hr>

    <ProductList
      v-if="!offerId"
      :catalog-id="catalogId"
      :selected="[]"
      :selected-listings.sync="selectedListings"
      only-listings
      single-select
    />

    <hr>
    <div class="row">
      <div class="col-6">
        <button
          class="btn btn-secondary btn-block"
          @click="$emit('cancel')"
        >
          Anuluj
        </button>
      </div>
      <div class="col-6">
        <button
          class="btn btn-primary btn-block"
          :disabled="invalid"
          @click="save"
        >
          Zapisz
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/api/axios';
import ProductList from '@/pages/shop/order/ProductList';
import moment from 'moment';

const WEEK_DAY_OPTIONS = [
  { value: 1, text: 'Poniedziałek' },
  { value: 2, text: 'Wtorek' },
  { value: 3, text: 'Środa' },
  { value: 4, text: 'Czwartek' },
  { value: 5, text: 'Piątek' },
  { value: 6, text: 'Sobota' },
  { value: 0, text: 'Niedziela' },
];

export default {
  props: {
    catalogId: String,
    initDay: String,
    initHour: [Number, String],
    rule: Object,
    offerId: {
      type: String,
      default: null,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    multiDay: false,
    selectedListings: [],
    hourFrom: '9:00',
    hourTo: '9:00',
    comment: '',
    removePending: false,
    savePending: false,
    editing: false,
    selectedWeekDay: 1,
    weekDayOptions: WEEK_DAY_OPTIONS,
  }),
  components: {
    ProductList,
  },
  computed: {
    formattedDay() {
      if (this.rule) {
        return moment(this.rule.day).format('dddd');
      }
      return moment(this.initDay).format('dddd');
    },
    resolvedOfferId() {
      return this.offerId || (this.selectedListings.length > 0 ? this.selectedListings[0] : null);
    },
    resolvedWeekDay() {
      return this.initDay ? moment(this.initDay).day() : this.selectedWeekDay;
    },
    invalid() {
      if (!this.resolvedOfferId) {
        return true;
      }
      return !this.validateHour(this.hourFrom) || !this.validateHour(this.hourTo, this.hourFrom);
    },
    editInvalid() {
      return !this.validateHour(this.hourFrom) || !this.validateHour(this.hourTo, this.hourFrom);
    },
  },
  methods: {
    startEditing() {
      this.hourFrom = this.formatAsTime(this.rule.startMinutes);
      this.hourTo = this.formatAsTime(this.rule.startMinutes + this.rule.durationMinutes);
      this.comment = this.rule.comment || '';
      this.editing = true;
    },
    async save() {
      if (!this.resolvedOfferId) {
        return;
      }

      await axios.post(`/calendar/${this.catalogId}/rules?offerId=${this.resolvedOfferId}`, {
        offerId: this.resolvedOfferId,
        weekDay: this.resolvedWeekDay,
        repeatType: 'Week',
        repeatCount: 1,
        repeatFrom: moment().startOf('week').format(),
        startMinutes: this.toMinutes(this.hourFrom),
        durationMinutes: this.toMinutes(this.hourTo) - this.toMinutes(this.hourFrom),
        comment: this.comment || null,
      });

      this.$emit('refresh');
      this.$emit('cancel');
    },
    async saveEdit() {
      this.savePending = true;
      try {
        await axios.put(`/calendar/${this.catalogId}/rules/${this.rule.id}?offerId=${this.rule.offerId}`, {
          startMinutes: this.toMinutes(this.hourFrom),
          durationMinutes: this.toMinutes(this.hourTo) - this.toMinutes(this.hourFrom),
          comment: this.comment || null,
        });

        this.$emit('refresh');
        this.$emit('cancel');
      } finally {
        this.savePending = false;
      }
    },
    async remove() {
      this.removePending = true;
      await axios.delete(`/calendar/${this.catalogId}/rules/${this.rule.id}?offerId=${this.rule.offerId}`);

      this.removePending = false;
      this.$emit('refresh');
      this.$emit('cancel');
    },
    formatHour(minutes) {
      const hour = Math.floor(minutes / 60).toString().padStart(2, '0');
      const minute = (minutes % 60).toString().padStart(2, '0');
      return `${hour}:${minute}`;
    },
    validateHour(hour, afterTime) {
      const h = this.toMinutes(hour);
      if (h < 0) {
        return false;
      }
      if (afterTime) {
        return h >= this.toMinutes(afterTime);
      }

      return true;
    },
    toMinutes(time) {
      if (!time) {
        return -1;
      }

      const parts = time.split(':');
      if (parts.length !== 2) {
        return -1;
      }
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      if (Number.isNaN(hours) || hours > 23 || Number.isNaN(minutes) || minutes > 59) {
        return -1;
      }
      return hours * 60 + minutes;
    },
    formatAsTime(minutes) {
      const hour = Math.floor(minutes / 60).toString().padStart(2, '0');
      const minute = (minutes % 60).toString().padStart(2, '0');
      return `${hour}:${minute}`;
    },
    updateHourFrom() {
      const minutes = this.toMinutes(this.hourFrom);
      if (minutes < 0) {
        return;
      }

      this.hourFrom = this.formatAsTime(minutes);
    },
    updateHourTo() {
      const minutes = this.toMinutes(this.hourTo);
      if (minutes < 0) {
        return;
      }
      this.hourTo = this.formatAsTime(minutes);
    },
  },
  created() {
    this.hourFrom = `${this.initHour}:00`;
    this.hourTo = `${this.initHour + 1}:00`;
    this.updateHourFrom();
    this.updateHourTo();
    if (this.editMode && this.rule) {
      this.startEditing();
    }
  },
};
</script>
